import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertCircle, ChevronRight, Printer, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/config/constants";
import { useOfflineData } from "@/contexts/OfflineDataContext";
import InfoBox from "@/components/protocol/InfoBox";
import ExpandableImageGallery from "@/components/protocol/ExpandableImageGallery";

export default function ProcedureDetail() {
  const { id } = useParams();
  const { getProcedure } = useOfflineData();
  const [procedure, setProcedure] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProcedure = async () => {
      try {
        const data = await getProcedure(id);
        setProcedure(data);
      } catch (error) {
        console.error("Error fetching procedure:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProcedure();
  }, [id, getProcedure]);

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  if (!procedure) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold">Procedure not found</h2>
        <p className="text-muted-foreground">The procedure you're looking for doesn't exist.</p>
        <Link to="/procedures" className="mt-4 inline-block">
          <Button>Back to Procedures</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <Link to="/procedures" className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 mb-2 no-print">
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Procedures
          </Link>
          <h1 className="font-heading text-xl sm:text-2xl font-bold break-words">{procedure.name}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            {procedure.care_level && (
              <Badge variant="default">{procedure.care_level.toUpperCase()} Only</Badge>
            )}
            <Badge variant="outline">Procedure</Badge>
          </div>
        </div>
        <Button variant="outline" size="icon" onClick={() => window.print()} className="no-print shrink-0 self-start" data-testid="procedure-print-btn">
          <Printer className="h-4 w-4" />
        </Button>
      </div>

      {/* Info Boxes */}
      {procedure.info_boxes?.map((box, i) => (
        <InfoBox key={i} type={box.type} title={box.title} content={box.content} image={box.image} imageAlt={box.imageAlt} />
      ))}

      {/* Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Procedure Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            {procedure.steps?.map((step, index) => (
              <li key={index} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium">{step.text}</p>
                  {step.sub_steps?.length > 0 && (
                    <ul className="mt-2 ml-4 space-y-1">
                      {step.sub_steps.map((sub, subIndex) => (
                        <li key={subIndex} className="text-sm text-muted-foreground flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full" />
                          {sub}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <ExpandableImageGallery images={procedure.images} />

      {/* Related Protocols */}
      {procedure.related_protocols?.length > 0 && (
        <Card data-testid="procedure-related-protocols">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Used In Protocols ({procedure.related_protocols.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {procedure.related_protocols.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/protocol/${rp.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors text-sm font-medium"
                >
                  <span className="text-primary">{rp.name}</span>
                  <ChevronRight className="w-3 h-3 text-primary" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {procedure.source_ref && (
        <Card className="border-muted">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Source Document
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="text-sm">
                <p className="font-medium text-foreground">{procedure.source_ref.document}</p>
                <p className="text-muted-foreground">
                  Section: {procedure.source_ref.section} | Pages: {procedure.source_ref.pages}
                </p>
              </div>
              <a
                href={`${API}/document/download`}
                download
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Download className="w-4 h-4" />
                Download Source Document
              </a>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
